import React, { useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Animated,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';

interface SwitchProps {
  label?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  style?: ViewStyle;
}

const TRACK_WIDTH = 32;
const TRACK_HEIGHT = 18;
const THUMB_SIZE = 14;
const THUMB_TRAVEL = 14; // distance thumb moves when checked

export default function Switch({
  label,
  checked = false,
  onChange,
  disabled = false,
  style,
}: SwitchProps) {
  const { theme } = useTheme();
  const { colors, fontSize } = theme;

  const thumbAnim = useRef(new Animated.Value(checked ? THUMB_TRAVEL : 0)).current;

  useEffect(() => {
    Animated.timing(thumbAnim, {
      toValue: checked ? THUMB_TRAVEL : 0,
      duration: 180,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [checked, thumbAnim]);

  // Single RN code path (renders on web via react-native-web and on native).
  const trackBg = checked ? colors.accent : colors.paper3;
  const trackBorder = checked ? colors.accent : colors.lineStrong;

  return (
    <TouchableOpacity
      onPress={disabled ? undefined : () => onChange?.(!checked)}
      disabled={disabled}
      activeOpacity={0.8}
      style={[styles.container, { opacity: disabled ? 0.55 : 1 }, style]}
      accessibilityRole="switch"
      accessibilityState={{ checked, disabled }}
    >
      <View style={[
        styles.track,
        {
          backgroundColor: trackBg,
          borderColor: trackBorder,
        },
      ]}>
        <Animated.View
          style={[
            styles.thumb,
            {
              backgroundColor: colors.card,
              transform: [{ translateX: thumbAnim }],
            },
          ]}
        />
      </View>
      {label && (
        <Text style={{
          fontSize: fontSize.sm,
          color: colors.ink2,
          fontFamily: theme.font.sans,
          marginLeft: 8,
        }}>
          {label}
        </Text>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  track: {
    width: TRACK_WIDTH,
    height: TRACK_HEIGHT,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    paddingHorizontal: 1,
  },
  thumb: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    shadowColor: '#101828',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.18,
    shadowRadius: 1,
    elevation: 1,
  },
});
